import { css } from '@emotion/core'
import { ContentRemove, NoticeDoNot } from '@fs/zion-icon/dist/cjs/icons'
import {
  ActionChip,
  Button,
  Cell,
  Checkbox,
  colors,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  FlowGrid,
  Grid,
  Row,
  useOverlay,
  H5,
} from '@fs/zion-ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getFilteredPersonChanges, getPersonChanges } from '../../../services/personService'
import { RawData } from '../../common/RawData'
import useNotifications from '../../../hooks/useNotifications'

const clickable = css`
  cursor: pointer;
`
export default function PersonChangesFilters({
  personId,
  personChangesState,
  personChangesDispatch,
  setRerenderInfiniteScroll,
  filtersState,
  setFiltersState,
}) {
  const overlay = useOverlay()
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [noFilterSelected, setNoFilterSelected] = useState(true)

  /**
   * Helper Functions
   */
  const updatePersonChangesData = (data) => {
    const action = {
      type: 'setPersonChanges',
      state: data,
    }
    setRerenderInfiniteScroll((prevState) => !prevState)
    personChangesDispatch(action)
  }

  // Any of the filter fetches below can be rejected by the API — an HTTP 400 whenever a subtype
  // value it is given is not an exact member of that filter's enum, for instance. Every one of them
  // needs this handler: without it the rejection escapes as an unhandled promise rejection, which
  // reaches the user as a silently unchanged table and reaches Sentry as a context-free
  // `AxiosError: Request failed with status code 400`. See DATA-12263.
  const notifyFilterError = (err) => {
    error(
      t('person.changes.filter.error', `Error applying filter: {message}`, {
        message: err.response?.data?.notifications?.[0]?.msg || err.message,
      })
    )
  }

  useEffect(() => {
    setNoFilterSelected(
      !Object.keys(filtersState)
        .filter((dropdownKey) => dropdownKey !== 'currentFilters')
        .some((dropdownKey) => {
          const dropdownItem = filtersState[dropdownKey]
          if (dropdownItem.isOpen) {
            return Object.keys(dropdownItem.accordionItems).some((accordionKey) => {
              const accordionItem = dropdownItem.accordionItems[accordionKey]
              return !!accordionItem.isChecked
            })
          }
          return false
        })
    )
  }, [filtersState])

  /**
   * Event Handlers
   */

  function checkboxOnChange(dropdownKey, accordionKey, event) {
    event.stopPropagation()
    const accordionItems = filtersState[dropdownKey].accordionItems
    if (dropdownKey === 'contributorId' || dropdownKey === 'conclusionType') {
      Object.keys(accordionItems).forEach((accordionkey) => {
        const accordionItem = accordionItems[accordionkey]
        accordionItem.isChecked = false
      })
    }
    accordionItems[accordionKey].isChecked = event.target.checked
    setFiltersState(filtersState)
  }

  const applyFiltersButtonOnClick = () => {
    const filters = {
      type: '',
      subtypes: [],
    }
    Object.keys(filtersState)
      .filter((dropdownKey) => dropdownKey !== 'currentFilters')
      .forEach((dropdownKey) => {
        const dropdownItem = filtersState[dropdownKey]
        if (dropdownItem.isOpen) {
          filters.type = dropdownKey
          Object.keys(dropdownItem.accordionItems).forEach((accordionKey) => {
            const accordionItem = dropdownItem.accordionItems[accordionKey]
            if (accordionItem.isChecked) {
              filters.subtypes.push(accordionItem.value)
            }
          })
        }
      })
    filtersState.currentFilters = filters
    filtersState.currentFilters.subtypes = filtersState.currentFilters.subtypes.map((subtype) => {
      const subTypeWithLabel = { subtype, label: '' }
      switch (filtersState.currentFilters.type) {
        case 'accessControls': {
          subTypeWithLabel.label = t('personChanges.accessControls', 'Access Controls')
          break
        }
        case 'contributorId': {
          subTypeWithLabel.label = t('personChanges.contributorId', 'Contributor ID: {{subtype}}', { subtype })
          break
        }
        case 'omitContributorId': {
          subTypeWithLabel.label = t('personChanges.omitContributorId', 'Omit Contributor ID: {{subtype}}', { subtype })
          break
        }
        default:
          subTypeWithLabel.label = subtype
          break
      }
      return subTypeWithLabel
    })
    setFiltersState(filtersState)
    getFilteredPersonChanges(
      personId,
      filters.type,
      filtersState.currentFilters.subtypes.map((subtype) => subtype.subtype)
    )
      .then((response) => {
        updatePersonChangesData(response.data)
      })
      .catch(notifyFilterError)
    overlay.close()
  }

  const clearFiltersButtonOnClick = () => {
    Object.keys(filtersState)
      .filter((dropdownKey) => dropdownKey !== 'currentFilters')
      .forEach((dropdownKey) => {
        const dropdownItem = filtersState[dropdownKey]
        dropdownItem.isOpen = false
        Object.keys(dropdownItem.accordionItems).forEach((accordionKey) => {
          const accordionItem = dropdownItem.accordionItems[accordionKey]
          accordionItem.isChecked = false
        })
      })
    filtersState.currentFilters = {
      type: '',
      subtypes: [],
    }
    setFiltersState(filtersState)
    getPersonChanges(personId)
      .then((response) => {
        updatePersonChangesData(response.data)
      })
      .catch(notifyFilterError)
    overlay.close()
  }

  const onFilterChipClick = (subtype) => {
    filtersState.currentFilters.subtypes = filtersState.currentFilters.subtypes.filter(
      (currentFiltersSubType) => currentFiltersSubType.subtype !== subtype
    )
    const itemToDeselect = Object.values(filtersState[filtersState.currentFilters.type].accordionItems).find(
      (item) => item.value === subtype
    )
    itemToDeselect.isChecked = false
    if (filtersState.currentFilters.subtypes.length > 0) {
      getFilteredPersonChanges(
        personId,
        filtersState.currentFilters.type,
        filtersState.currentFilters.subtypes.map((s) => s.subtype)
      )
        .then((response) => {
          updatePersonChangesData(response.data)
        })
        .catch(notifyFilterError)
    } else {
      filtersState.currentFilters.type = ''
      getPersonChanges(personId)
        .then((response) => {
          updatePersonChangesData(response.data)
        })
        .catch(notifyFilterError)
    }
    setFiltersState(filtersState)
  }

  function expansionPanelOnClick(currentDropdownItem) {
    Object.keys(filtersState)
      .filter((dropdownKey) => dropdownKey !== 'currentFilters')
      .forEach((dropdownKey) => {
        const dropdownItem = filtersState[dropdownKey]
        if (dropdownItem.label === currentDropdownItem.label) {
          dropdownItem.isOpen = !currentDropdownItem.isOpen // toggle
        } else {
          // clean the rest
          dropdownItem.isOpen = false
          Object.keys(dropdownItem.accordionItems).forEach((accordionKey) => {
            const accordionItem = dropdownItem.accordionItems[accordionKey]
            accordionItem.isChecked = false
          })
        }
      })
    setFiltersState(filtersState)
  }

  /**
   * Preparing Stuff before rendering
   */

  useEffect(() => {
    const idsMetadata = personChangesState.idsMetadata
    if (idsMetadata) {
      Object.keys(idsMetadata).forEach((id) => {
        const item = idsMetadata[id]
        if (item.type === 'CONTRIBUTOR' || item.type === 'CONTRIBUTOR_AND_SUBMITTER') {
          const accordionItem = {
            label: `${item.friendlyName} (${id})`,
            value: id,
            isChecked: false,
            accountIsEnabled: item.accountEnabled,
          }
          filtersState.contributorId.accordionItems[id] = accordionItem
          filtersState.omitContributorId.accordionItems[id] = accordionItem
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personChangesState.idsMetadata])

  return (
    <div>
      <Row alignY="middle">
        <Button id="filtersBtn" emphasis="medium" onClick={overlay.handleClick} noShrinkX>
          {t('filters.label', 'Filters')}
        </Button>
        <Row wrap gutters="nano">
          {filtersState.currentFilters.subtypes.map((subType) => (
            <ActionChip
              id={`${subType.label}_chip`}
              key={subType.label}
              Icon={ContentRemove}
              label={subType.label}
              onClick={() => onFilterChipClick(subType.subtype)}
            />
          ))}
        </Row>
      </Row>
      <DialogOverlay title="" {...overlay} size="xl">
        <Grid>
          {/* Row */}
          <Cell columns={12}>
            {Object.keys(filtersState)
              .filter((dropdownKey) => dropdownKey !== 'currentFilters')
              .map((dropdownKey) => {
                const dropdownItem = filtersState[dropdownKey]
                return (
                  <div key={dropdownKey} data-testid={`expander-${dropdownItem.label}`}>
                    <div css={clickable}>
                      <H5
                        data-testid={`button-${dropdownItem.label}`}
                        onClick={() => expansionPanelOnClick(dropdownItem)}
                      >
                        {dropdownItem.label}
                      </H5>
                    </div>
                    {dropdownItem.isOpen && (
                      <div>
                        <FlowGrid columnCount={3} orientation="vertical" gutters="md">
                          {Object.keys(dropdownItem.accordionItems).map((accordionKey) => {
                            const accordionItem = dropdownItem.accordionItems[accordionKey]
                            return (
                              <React.Fragment key={accordionItem.label}>
                                <Checkbox
                                  id={accordionItem.value}
                                  name={accordionItem.value}
                                  label={
                                    <>
                                      {accordionItem.accountIsEnabled === false && (
                                        <>
                                          <NoticeDoNot color={colors.red50} data-testid="disabledAccountIcon" />
                                          {` `}
                                        </>
                                      )}
                                      {accordionItem.label}
                                    </>
                                  }
                                  checked={accordionItem.isChecked}
                                  color="primary"
                                  onChange={(event) => checkboxOnChange(dropdownKey, accordionKey, event)}
                                />
                              </React.Fragment>
                            )
                          })}
                        </FlowGrid>
                      </div>
                    )}
                  </div>
                )
              })}
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            <RawData state={filtersState} />
          </Cell>
        </Grid>

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              id="applyFiltersBtn"
              onClick={applyFiltersButtonOnClick}
              emphasis="high"
              disabled={noFilterSelected}
            >
              {t('person.buttons.applyFilters', 'Apply Filters')}
            </Button>
            <Button onClick={clearFiltersButtonOnClick} emphasis="medium">
              {t('filters.clear', 'Clear Filters')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </div>
  )
}
